import type { ActionListOption } from '@latitude/action-list';
import { ActionList } from '@latitude/action-list';
import { Stack } from '@latitude/layout';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { TriggerInput } from 'components/TriggerInput';
import { TriggerWrapper } from 'components/TriggerWrapper';
import { useMemo } from 'react';

import { getTriggerInputClassNames } from './helpers';
import { useSyncOptions } from './hooks/sync/useSyncOptions';
import { type SyncSelectProps } from './types';
import { disableNonSelectedOptions } from './utils';

export const SyncSelect = <
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  T = undefined,
>({
  withSearch = true,
  isMulti,
  isNullable,
  triggerClassName,
  inputClassName,
  inputContentWrapperClassName,
  itemSize = 'medium',
  value,
  onChange,
  trailingElement,
  placeholder,
  options,
  disabled,
  searchPlaceholder = 'Search...',
  error,
  searchInputError,
  searchInputErrorMessage,
  ref,
  'data-testid': testID,
  isExternalSearch,
  readOnly,
  onBlur,
  rawProps,
  multiSelectOverflow = 'overflowStack',
  hasMaxHeight = true,
  maxHeight = 80,
  defaultSearchTerm = '',
  allowAddItem = false,
  addItemLabel = undefined,
  onSearchChange: onSearchChangeProp,
  debounceSearchDelay = 0,
  maxSelections,
  selectionLimitReachedLabel = 'Selection limit reached',
  ...props
}: SyncSelectProps<isMulti, isNullable, T>) => {
  const {
    open,
    setOpen,
    resolvedOptions,
    inputPlaceholder,
    debouncedSearchTerm,
    showAddItemOption,
    onSearchChange,
    handleDismiss,
    handleTriggerKeyDown,
    OptionComponent,
    wrapperStyle,
  } = useSyncOptions({
    options,
    isMulti,
    value,
    placeholder,
    defaultSearchTerm,
    debounceSearchDelay,
    allowAddItem,
    addItemLabel,
    onSearchChange: onSearchChangeProp,
    OptionComponent: props.OptionComponent,
    disabled,
    onBlur,
    multiSelectOverflow,
    hasMaxHeight,
    maxHeight,
  });

  const showSelectionsCounter = isMulti && maxSelections;
  const selectedValues = showSelectionsCounter ? (value as string[]) : [];
  const limitReached =
    showSelectionsCounter && selectedValues.length >= maxSelections;

  const cappedOptions = useMemo(
    () =>
      limitReached
        ? disableNonSelectedOptions(resolvedOptions, selectedValues)
        : resolvedOptions,
    [limitReached, resolvedOptions, selectedValues],
  );

  const actionList = (
    <ActionList
      {...props}
      open={!readOnly && open}
      setOpen={setOpen}
      options={cappedOptions as ActionListOption<T>[]}
      withSearch={withSearch}
      isMulti={isMulti}
      isNullable={isNullable}
      onChange={onChange}
      placeholder={searchPlaceholder}
      value={value}
      disabled={disabled}
      itemSize="small"
      data-testid={testID}
      isExternalSearch={isExternalSearch}
      error={error || searchInputError}
      errorMessage={searchInputErrorMessage}
      defaultSearchTerm={debouncedSearchTerm}
      onSearchChange={onSearchChange}
      OptionComponent={OptionComponent}
      clearSearchOnClose={showAddItemOption ? false : props.clearSearchOnClose}
      onDismiss={handleDismiss}
      rawProps={rawProps}
    >
      <TriggerWrapper
        aria-disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(triggerClassName, 'group')}
        style={wrapperStyle}
        tabIndex={disabled || readOnly ? -1 : 0}
        onKeyDown={handleTriggerKeyDown}
      >
        <TriggerInput
          disabled={disabled}
          inputSize={itemSize}
          classNames={getTriggerInputClassNames(
            disabled,
            inputClassName,
            inputContentWrapperClassName,
          )}
          ref={ref}
          trailingElement={trailingElement}
          clearable={isMulti}
          clickOnly
          readOnly={readOnly}
          {...(!isMulti && { debounce: 0 })}
          direction={open ? 'up' : 'down'}
          isMulti={isMulti}
          isNullable={isNullable}
          multiSelectOverflow={multiSelectOverflow}
          hasMaxHeight={hasMaxHeight}
          data-testid={testID ? `${testID}-input` : undefined}
          placeholder={inputPlaceholder}
          aria-label={placeholder}
          error={error}
          onBlur={onBlur}
        />
      </TriggerWrapper>
    </ActionList>
  );

  if (!showSelectionsCounter) {
    return actionList;
  }

  return (
    <Stack gap={4}>
      {actionList}
      <Stack
        direction="horizontal"
        justify="between"
        data-testid="select-selections-counter"
      >
        <Text variant="captionRegular" color="muted">
          {limitReached ? selectionLimitReachedLabel : ''}
        </Text>
        <Text variant="captionRegular" color="muted">
          {selectedValues.length}/{maxSelections}
        </Text>
      </Stack>
    </Stack>
  );
};

SyncSelect.displayName = 'SyncSelect';
